import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useTheme } from "../lib/theme";
import { WelcomeGuide } from "../components/WelcomeGuide";
import { Coins, Cross, Circle, FlowerLotus, Package, GridFour, Camera, Medal } from "@phosphor-icons/react";

const PALETTE_LIGHT = {
  "Tutto": { color: "#1E2520", accent: "#E8E1D2" },
  "Moneta o Medaglia": { color: "#9C5B42", accent: "#F0E4D8" },
  "Devozionale": { color: "#7A4632", accent: "#EFE3D5" },
  "Bottone": { color: "#5C635A", accent: "#E8E5DD" },
  "Anello": { color: "#8B6E4B", accent: "#EFE8D8" },
  "Militaria": { color: "#3F4A3A", accent: "#E1E4DA" },
  "Vario": { color: "#4A5043", accent: "#E5E5DD" },
};
const PALETTE_DARK = {
  "Tutto": { color: "#EDE6D6", accent: "#2C2825" },
  "Moneta o Medaglia": { color: "#C97D5C", accent: "#2A2520" },
  "Devozionale": { color: "#D08F73", accent: "#2A2521" },
  "Bottone": { color: "#9DA39A", accent: "#23211F" },
  "Anello": { color: "#C09F76", accent: "#2A2520" },
  "Militaria": { color: "#9CAE92", accent: "#21221F" },
  "Vario": { color: "#7E8579", accent: "#22211F" },
};

const ICON_BY_CATEGORY = {
  "Moneta o Medaglia": Coins,
  "Devozionale": Cross,
  "Bottone": Circle,
  "Anello": FlowerLotus,
  "Militaria": Medal,
  "Vario": Package,
};

// Mini-descrizioni mostrate sulle card categoria — chiariscono al volo
// cosa va dove, così l'utente capisce dove cercare un reperto specifico.
const DESCRIPTION_BY_CATEGORY = {
  "Moneta o Medaglia":
    "Monete antiche e moderne, gettoni, medaglie commemorative o premio.",
  "Devozionale":
    "Crocifissi, medagliette religiose, santini, reliquiari, oggetti di culto.",
  "Bottone":
    "Bottoni in metallo: militari, divise, abiti d'epoca, livree.",
  "Anello":
    "Anelli e fedi, sigillari, anelli votivi o ornamentali.",
  "Militaria":
    "Reperti bellici di WWI/WWII: distintivi, gradi, fibbie, ogive, schegge.",
  "Vario":
    "Tutto il resto: chiavi, fibbie, ornamenti, frammenti non identificabili in altra categoria.",
};

export default function Home() {
  const { theme } = useTheme();
  const palette = theme === "dark" ? PALETTE_DARK : PALETTE_LIGHT;
  const [data, setData] = useState({ categories: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/artifacts/categories");
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const tuttoMeta = palette["Tutto"];

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 fade-in">
      {!loading && data.total === 0 && <WelcomeGuide />}
      <div className="mb-10 md:mb-14">
        <div className="label-eyebrow mb-3">Catalogo</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1E2520]">
          Scegli una categoria
        </h1>
        <p className="text-[#5C635A] mt-3 max-w-2xl">
          Ogni reperto è classificato in una di sei categorie e numerato con un codice unico
          (es: <span className="font-mono text-[#9C5B42]">M1, D3, V7</span>) che puoi riportare sulla bustina
          di custodia per ritrovarlo in un attimo.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card aspect-[5/3] scanning" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {/* Tutto first */}
          <Link
            to="/catalog/tutto"
            data-testid="cat-tile-tutto"
            className="card group relative overflow-hidden p-7 md:p-8 hover:-translate-y-1 transition-transform duration-300"
            style={{ backgroundColor: tuttoMeta.accent }}
          >
            <div className="flex items-start justify-between mb-6">
              <span className="inline-flex h-12 w-12 items-center justify-center bg-[#1E2520] text-white">
                <GridFour weight="duotone" size={26} />
              </span>
              <span className="font-mono text-xs tracking-[0.2em]" style={{ color: tuttoMeta.color }}>TUTTI</span>
            </div>
            <div className="font-serif text-3xl mb-2 text-[#1E2520]">Tutto</div>
            <div className="text-sm text-[#5C635A] mb-2">Vista completa del catalogo</div>
            <div className="text-sm font-mono" style={{ color: tuttoMeta.color }}>
              {data.total} repert{data.total === 1 ? "o" : "i"}
            </div>
          </Link>

          {data.categories.map((c) => {
            const meta = palette[c.id] || palette["Vario"];
            const Icon = ICON_BY_CATEGORY[c.id] || Package;
            return (
              <Link
                key={c.id}
                to={`/catalog/${encodeURIComponent(c.id)}`}
                data-testid={`cat-tile-${c.prefix}`}
                className="card group relative overflow-hidden p-7 md:p-8 hover:-translate-y-1 transition-transform duration-300"
                style={{ backgroundColor: meta.accent }}
              >
                <div className="flex items-start justify-between mb-6">
                  <span
                    className="inline-flex h-12 w-12 items-center justify-center text-white"
                    style={{ backgroundColor: meta.color }}
                  >
                    <Icon weight="duotone" size={26} />
                  </span>
                  <span
                    className="font-mono text-xs tracking-[0.2em]"
                    style={{ color: meta.color }}
                  >
                    PREFISSO {c.prefix}
                  </span>
                </div>
                <div className="font-serif text-3xl text-[#1E2520] mb-2">{c.id}</div>
                <div className="text-sm text-[#5C635A] mb-3 min-h-[2.5rem]" data-testid={`cat-desc-${c.prefix}`}>
                  {DESCRIPTION_BY_CATEGORY[c.id] || ""}
                </div>
                <div className="text-sm font-mono" style={{ color: meta.color }}>
                  {c.count} repert{c.count === 1 ? "o" : "i"}
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {data.total === 0 && !loading && (
        <div className="mt-12 border border-dashed border-[#1E2520]/15 p-10 md:p-14 text-center bg-[#F0EBE1]/40">
          <h3 className="font-serif text-3xl text-[#1E2520] mb-2">Inizia il tuo catalogo</h3>
          <p className="text-[#5C635A] mb-6 max-w-md mx-auto">
            Identifica il primo reperto. Riceverà automaticamente un codice univoco.
          </p>
          <Link to="/scan" className="btn-primary inline-flex items-center gap-2" data-testid="home-scan-btn">
            <Camera weight="duotone" size={18} /> Identifica un reperto
          </Link>
        </div>
      )}
    </div>
  );
}
